import MuiTypography from '@mui/material/Typography';
import CalendarIcon from '@mui/icons-material/CalendarMonth';
import { LinearProgress, Stack, Chip, Tooltip, Typography, Checkbox } from '@mui/material';
import moment from 'moment';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import dayjs from 'dayjs';
import { Fragment } from 'react';
import RectangleChip from "../../../components/common/RectangleChip.component";
import { DeliveryYachtsStatusLogbookListEdit } from '../../../components/dispatchBoard/DeliveryYachtsStatusLogbookListEdit.component';

const PICKUP_STATUS_TONE = {
  completed: {
    label: 'COMPLETED',
    chipText: '#1B5E20',
    chipBg: '#E8F5E9',
    chipBorder: '#A5D6A7',
    progress: '#22C55E',
  },
  in_progress: {
    label: 'IN PROGRESS',
    chipText: '#8A5A00',
    chipBg: '#FFF8E1',
    chipBorder: '#FFD58A',
    progress: '#F59E0B',
  },
  not_started: {
    label: 'NOT STARTED',
    chipText: '#B71C1C',
    chipBg: '#FFEBEE',
    chipBorder: '#EF9A9A',
    progress: '#EF4444',
  },
  no_pickups: {
    label: 'NO PICKUPS',
    chipText: '#37474F',
    chipBg: '#ECEFF1',
    chipBorder: '#B0BEC5',
    progress: '#B0BEC5',
  },
};

const getPickupMetrics = (status) => {
  const total = status.total;
  const completedCount = status.completed;
  const percentage = status.percentage;

  if (total > 0 && completedCount === total) {
    return {
      total,
      completedCount,
      percentage,
      tone: PICKUP_STATUS_TONE.completed,
    };
  }

  if (completedCount > 0) {
    return {
      total,
      completedCount,
      percentage,
      tone: PICKUP_STATUS_TONE.in_progress,
    };
  }

  if (total > 0) {
    return {
      total,
      completedCount,
      percentage,
      tone: PICKUP_STATUS_TONE.not_started,
    };
  }

  return {
    total,
    completedCount,
    percentage: 100,
    tone: PICKUP_STATUS_TONE.no_pickups,
  };
};

const PickupStatusCell = ({ status }) => {
  const metrics = getPickupMetrics(status);
  let chipValue = `${metrics.tone.label}`;
  if (metrics.total > 0) {
    chipValue += ` (${metrics.completedCount}/${metrics.total})`;
  }

  return (
    <RectangleChip
      width='auto'
      value={chipValue}
      color={metrics.tone.chipText}
      backgroundColor={metrics.tone.chipBg}
      sx={{
        border: `1px solid ${metrics.tone.chipBorder}`,
        boxShadow: 0,
      }}
    />
  );
};

const CompletionRateCell = ({ status }) => {
  const metrics = getPickupMetrics(status);

  return (
    <Stack direction='row' spacing={1.25} alignItems='center' sx={{ minWidth: 210 }}>
      <LinearProgress
        variant='determinate'
        value={metrics.percentage}
        sx={{
          flexGrow: 1,
          height: 8,
          borderRadius: 999,
          backgroundColor: '#E5E7EB',
          '& .MuiLinearProgress-bar': {
            borderRadius: 999,
            backgroundColor: metrics.tone.progress,
          },
        }}
      />
      <Typography
        variant='body2'
        sx={{ fontWeight: 700, minWidth: 36, textAlign: 'right' }}
      >
        {metrics.percentage}%
      </Typography>
    </Stack>
  );
};

export const ListConf = {
  initialState: {
    pagination: {
      page: 1,
      pageSize: 25,
    },

    sort: [
      {
        by: 'collection_delivery_date',
        direction: 'asc',
      },
      {
        by: 'collection_delivery_time',
        direction: 'asc',
      },
    ],
  },
  rowsPerPageOptions: [25, 50, 100],
  columns: [
    {
      id: 'checkbox_select',
      key: 'checkbox_select',
      sort_key: '',
      sortable: false,
      label: '',
      checkbox: true,
      width: 40,
      selectable: true,
    },
    {
      id: 'attributes.status_logbook_id',
      key: 'status_logbook_id',
      sort_key: 'status_logbook',
      sortable: true,
      label: 'DELIVERY STATUS',
      width: 150,
    },
    {
      id: 'attributes.id_departments',
      key: 'id_departments',
      sort_key: 'pickup_departments',
      sortable: true,
      label: 'DEPARTMENT',
      width: 120,
    },
    {
      id: 'attributes.id_yacht.data',
      key: 'yacht_name',
      sort_key: 'id_yacht.yacht_name',
      sortable: true,
      label: 'YACHT NAME',
      width: 180,
    },
    {
      id: 'attributes.collection_delivery_date_id',
      key: 'collection_delivery_date_id',
      sort_key: 'collection_delivery_date',
      sortable: true,
      label: 'DATE',
      width: 150,
    },
    {
      id: 'attributes.collection_delivery_time_id',
      key: 'collection_delivery_time_id',
      sort_key: 'collection_delivery_time',
      sortable: true,
      label: 'TIME',
      width: 120,
    },
    {
      id: 'attributes.id_location.data.attributes.marina',
      key: 'id_location',
      sort_key: 'id_location.marina',
      sortable: true,
      label: 'LOCATION',
      width: 150,
    },
    {
      id: 'attributes.id_pickup_status',
      key: 'id_pickup_status',
      sort_key: 'pickup_status',
      sortable: false,
      label: 'PICKUP STATUS',
      width: 180,
    },
    {
      id: 'attributes.id_completion_percentage',
      key: 'id_completion_percentage',
      sort_key: 'completion_percentage',
      sortable: false,
      label: 'COMPLETION RATE (%)',
      width: 240,
    },
  ],
  render: {
    formatHead: (key, value, { isChecked, indeterminate }) => {
      switch (key) {
        case 'checkbox_select':
          return (
            <Checkbox
              checked={isChecked || false}
              color='primary'
              indeterminate={indeterminate}
            />
          );
        default:
          return (
            <MuiTypography
              variant='body1'
              sx={{ fontWeight: 'bold', color: 'white' }}
            >
              {value}
            </MuiTypography>
          );
      }
    },
    formatValue: (key, value, onClick, isChecked) => {
      switch (key) {

        case 'collection_delivery_time_id':
          return value ? (
            <Stack direction={'row'} spacing={1} alignItems={'center'}>
              {value?.collection_delivery_time ? (
                <Fragment>
                  <AccessTimeIcon color='action' />
                  <MuiTypography
                    sx={{ fontWeight: 'bold', height: 1 }}
                    variant='body1'
                    onClick={onClick}
                  >
                    {moment(
                      value.collection_delivery_time,
                      'HH:mm:ss'
                    ).format('HH:mm')}
                  </MuiTypography>
                </Fragment>
              ) : (
                '-'
              )}
            </Stack>
          ) : (
            <MuiTypography
              variant='body1'
              onClick={onClick}
              sx={{ height: '100%' }}
            >
              -
            </MuiTypography>
          );
        case 'collection_delivery_date_id':
          return <Stack direction={'row'} spacing={1} alignItems={'center'}>
            <CalendarIcon color='action' />
            <MuiTypography
              sx={{ fontWeight: 'bold', height: 1 }}
              variant='body1'
              onClick={onClick}
            >
              {value?.collection_delivery_date
                ? dayjs(value?.collection_delivery_date).format(
                  'DD-MM-YYYY'
                )
                : '-'}
            </MuiTypography>
          </Stack>

        case 'yacht_name': {
          const attendance =
            value?.attributes?.id_attendance?.data?.[0]?.attributes ||
            value?.attributes?.id_attendance?.data?.attributes ||
            value?.attributes?.id_yacht?.data?.attributes?.id_attendance?.data?.[0]?.attributes;
          const provenence =
            attendance?.id_provenence_list?.data?.attributes?.provenence?.data
              ?.attributes?.provenence ||
            value?.attributes?.provenence;
          const mexalCode = attendance?.mexal_code || '-';
          const mexalResearchName = attendance?.mexal_research_name || '-';
          const mexalTooltip = [mexalCode, mexalResearchName]
            .filter((item) => item && item !== '-')
            .join(' - ');

          return (
            <Tooltip title={mexalTooltip || ''}>
              <Stack
                direction={'row'}
                justifyContent={'space-between'}
                spacing={1}
              >
                <Stack direction={'column'} spacing={0.5}>
                  <MuiTypography
                    variant='body1'
                    sx={{ fontWeight: 'bold' }}
                    onClick={onClick}
                  >
                    {value?.attributes?.yacht_name || '-'}
                  </MuiTypography>
                  {provenence && (
                    <MuiTypography
                      variant='caption'
                      color='text.secondary'
                      onClick={onClick}
                    >
                      {provenence}
                    </MuiTypography>
                  )}
                </Stack>
                <Chip label={`#${value?.id || '-'}`} size='small' />
              </Stack>
            </Tooltip>
          );
        }

        case 'id_pickup_status': {
          return <PickupStatusCell status={value} />;
        }

        case 'id_completion_percentage': {
          return <CompletionRateCell status={value} />;
        }

        case 'id_departments': {
          return <MuiTypography variant='body1'>{value.pickup_departments || '-'}</MuiTypography>;
        }

        case 'status_logbook_id':
          return <DeliveryYachtsStatusLogbookListEdit value={value} onClick={onClick} stopPropagation={true} />;

        case 'checkbox_select':
          return (
            <Checkbox
              checked={isChecked || false}
              color='primary'
              disabled={onClick ? false : true}
            />
          );

        default:
          return (
            <MuiTypography
              variant='body1'
              onClick={onClick}
              sx={{ height: '100%' }}
            >
              {value || '-'}
            </MuiTypography>
          );
      }
    },
  },
};